<template>
    <v-card outlined>
        <v-card-title>{{ $t("Avainsanat") }}</v-card-title>
        <v-card-text>
            <v-combobox v-model="selectedTags" :items="availableTags" :search-input.sync="searchInput" density="compact"
                :loading="isLoading" :label="$t('Hae avainsanoja tai lisää uusi')" item-text="title" multiple chips
                deletable-chips outlined hide-details="auto" @input="onTagsChanged" @update:search-input="onSearchInput"
                return-object>
                <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip v-bind="attrs" :input-value="selected" close @click="select" @click:close="removeTag(item)"
                        color="primary" outlined>
                        {{ getTagTitle(item) }}
                    </v-chip>
                </template>
                <template v-slot:item="{ item }">
                    <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                </template>
                <template v-slot:no-data>
                    <v-list-item v-if="searchInput && searchInput.length > 0">
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t('Paina Enter luodaksesi uuden avainsanan') }}: "{{ searchInput }}"
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>
            </v-combobox>


        </v-card-text>

        <!-- Tag Form Dialog -->
        <TagFormDialog :show="showNewTagDialog" :key="pendingNewTagTitle != null ? pendingNewTagTitle : 'new-tag'"
            :tag="null" mode="create" :initial-title="pendingNewTagTitle" @save="onTagFormSave"
            @cancel="onTagFormCancel" @close="onTagFormClose" />
    </v-card>
</template>

<script>
import { ref, watch, onMounted, computed, inject } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import { i18n } from "@/plugins/i18n";
import TagFormDialog from "./TagFormDialog.vue";

export default {
    name: "TagSelector",
    components: {
        TagFormDialog
    },
    props: {
        value: {
            type: Array,
            default: () => []
        }
    },
    setup(props, { emit }) {
        const { t } = useTranslations();
        const { tenant } = useAuth();
        const selectedTags = ref([]);
        const availableTags = ref([]);
        const searchInput = ref("");
        const isLoading = ref(false);
        const searchTimeout = ref(null);

        // New tag dialog state
        const showNewTagDialog = ref(false);
        const pendingNewTagTitle = ref("");

        // Initialize selected tags from props
        watch(() => props.value, (newValue) => {

            selectedTags.value = [...newValue];
        }, { immediate: true });

        const searchTags = async (query = "") => {
            if (isLoading.value) return;

            isLoading.value = true;
            try {
                const response = await adminApi.getTags(query);
                if (response.ok) {
                    availableTags.value = response.data.results || response.data;
                }
            } catch (error) {
                console.error("Error searching tags:", error);
            } finally {
                isLoading.value = false;
            }
        };

        const onSearchInput = (value) => {
            // Clear previous timeout
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value);
            }

            // Debounce search
            searchTimeout.value = setTimeout(() => {
                if (value && value.length > 0) {
                    searchTags(value);
                } else {
                    searchTags();
                }
            }, 300);
        };

        const onTagsChanged = async (tags) => {
            const processedTags = [];

            for (const tag of tags) {
                if (typeof tag === 'string') {
                    // This is a new tag - open dialog for translation
                    pendingNewTagTitle.value = tag;
                    openNewTagDialog(tag);
                    // Don't add the tag yet, wait for dialog completion
                    return;
                } else {
                    // This is an existing tag
                    processedTags.push(tag);
                }
            }

            selectedTags.value = processedTags;
            emit('input', processedTags);
        };

        const openNewTagDialog = (initialTitle) => {
            pendingNewTagTitle.value = initialTitle;
            showNewTagDialog.value = true;
        };

        const onTagFormSave = (savedTag) => {
            console.log('onTagFormSave called with:', savedTag);
            console.log('selectedTags before:', selectedTags.value);
            console.log('availableTags before:', availableTags.value);

            // Remove any string tags (pending new tags) from selectedTags first
            const cleanedTags = selectedTags.value.filter(tag => typeof tag !== 'string');

            // Add the new tag to selected tags
            cleanedTags.push(savedTag);

            // Force reactivity update by creating new array
            selectedTags.value = [...cleanedTags];
            emit('input', selectedTags.value);

            console.log('selectedTags after push:', selectedTags.value);

            // Clear search input to reset combobox state
            searchInput.value = "";

            // Force refresh of available tags to ensure consistency
            setTimeout(() => {
                searchTags();
            }, 100);

            // Close dialog
            showNewTagDialog.value = false;
            pendingNewTagTitle.value = "";

            console.log('Dialog closed');
        };

        const onTagFormCancel = () => {
            showNewTagDialog.value = false;
            pendingNewTagTitle.value = "";
            // Reset the combobox to remove the pending string tag
            selectedTags.value = selectedTags.value.filter(tag => typeof tag !== 'string');
        };

        const onTagFormClose = () => {
            showNewTagDialog.value = false;
            pendingNewTagTitle.value = "";
        };

        const removeTag = (tagToRemove) => {
            const index = selectedTags.value.findIndex(tag =>
                getTagKey(tag) === getTagKey(tagToRemove)
            );
            if (index > -1) {
                selectedTags.value.splice(index, 1);
                emit('input', selectedTags.value);
            }
        };

        const getTagTitle = (tag) => {
            return typeof tag === 'string' ? tag : tag.title;
        };

        const getTagKey = (tag) => {
            return typeof tag === 'string' ? tag : (tag.id || tag.title);
        };

        onMounted(() => {
            searchTags(); // Load initial tags
        });

        return {
            selectedTags,
            availableTags,
            searchInput,
            isLoading,
            onTagsChanged,
            onSearchInput,
            removeTag,
            getTagTitle,
            getTagKey,
            // Dialog state and methods
            showNewTagDialog,
            pendingNewTagTitle,
            openNewTagDialog,
            onTagFormSave,
            onTagFormCancel,
            onTagFormClose
        };
    }
};
</script>
